import { useEffect, useState } from 'react'
import BuildInfo from './components/BuildInfo'
import { refreshApp } from './lib/update'
import { t } from './lib/i18n'
import SignIn from './components/SignIn'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'
import Icon from './components/Icon'
import Discover from './components/sm/Discover'
import Import from './components/sm/Import'
import LibraryTable from './components/sm/LibraryTable'
import RecipeEditor from './components/sm/RecipeEditor'
import Cook from './components/sm/Cook'
import Kitchen from './components/sm/Kitchen'
import FeedbackButton from './components/sm/Feedback'
import Waiting from './components/sm/Waiting'
import { KitchenHero, MomentModal } from './components/sm/Game'
import { useAccountKitchen } from './hooks/useAccountKitchen'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

type Tab = 'discover' | 'library' | 'save' | 'kitchen' | 'account' | 'admin'
type Screen =
  | { at: 'tabs' }
  | { at: 'recipe'; id: string }
  | { at: 'cook'; recipeId: string; sessionId?: string }

export default function App() {
  const { user, health, ready, signingOut, recipes, categories, running, summary, buddy, setBuddy,
    moments, dismissMoment, refreshUser, logOut, reload } = useAccountKitchen()
  const [tab, setTab] = useState<Tab>('discover')
  const [screen, setScreen] = useState<Screen>({ at: 'tabs' })

  /* An installed app that is shared to arrives with ?shared=; go straight to the
     save screen so the share lands somewhere useful. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.has('shared')) setTab('save')
    /* A reminder about one recipe links to /?recipe=<id>. The app has no URL
       router, so the link is read once and dropped from the address bar. */
    const recipe = params.get('recipe')
    if (recipe) {
      setScreen({ at: 'recipe', id: recipe })
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  if (!ready) return <div className="boot">Warming the oven…</div>

  /* What she'd call the screen, not what the router calls it. */
  const screenName = screen.at === 'cook' ? 'Cooking'
    : screen.at === 'recipe' ? 'A recipe'
    : tab === 'discover' ? 'Discover'
    : tab === 'library' ? 'Library'
    : tab === 'save' ? 'Save'
    : tab === 'kitchen' ? 'Kitchen'
    : tab === 'account' ? 'Account'
    : 'Admin'

  const openRecipe = (id: string) => setScreen({ at: 'recipe', id })
  const cook = (recipeId: string, sessionId?: string) => setScreen({ at: 'cook', recipeId, sessionId })
  const backToTabs = async () => { setScreen({ at: 'tabs' }); await reload() }

  const TABS: [Tab, string, Parameters<typeof Icon>[0]['name']][] = [
    ['discover', 'Discover', 'sparkle'],
    ['library', 'Library', 'book'],
    ['save', 'Save', 'plus'],
    ['kitchen', 'Kitchen', 'heart'],
  ]

  return (
    <ErrorBoundary>
      <UpdateBar />
      <EnvBanner health={health} />

      <header className="masthead">
        {/* The mark, not the wide lockup — a 1452x601 image squeezed into 38px is mush. */}
        <img className="masthead-mark" src="/icon-192.png" alt="" width={38} height={38} />
        <span className="masthead-name">
          <b>Save n Make</b>
          <span>Turn inspiration into something delicious</span>
        </span>
        {user && (
          <nav>
            {TABS.map(([key, label, icon]) => (
              <button
                key={key} type="button" aria-current={tab === key && screen.at === 'tabs'}
                onClick={() => { setTab(key); setScreen({ at: 'tabs' }) }}
              >
                <Icon name={icon} size={15} /> <span className="nav-label">{label}</span>
              </button>
            ))}
            <button type="button" aria-current={tab === 'account' && screen.at === 'tabs'} onClick={() => { setTab('account'); setScreen({ at: 'tabs' }) }}>
              <Icon name="person" size={15} />
              <span className="sr-only">Account</span>
            </button>
            {user.capabilities.includes('admin.access') && (
              <button type="button" aria-current={tab === 'admin' && screen.at === 'tabs'} onClick={() => { setTab('admin'); setScreen({ at: 'tabs' }) }}>
                <Icon name="settings" size={15} />
                <span className="sr-only">Admin</span>
              </button>
            )}
            <button type="button" onClick={async () => { setScreen({ at: 'tabs' }); setTab('discover'); await logOut() }}>
              <Icon name="exit" size={15} /> <span className="nav-label">Sign out</span>
            </button>
          </nav>
        )}
      </header>

      <main className={tab === 'library' && screen.at === 'tabs' ? 'wide' : undefined}>
        {!user && (
          <>
            <div className="card" style={{ textAlign: 'center' }}>
              {/* The mark and the name set in type. public/logo.png is the old
                  "Save to Make" lockup, and showing it here put the wrong name on
                  the very first screen anyone sees. */}
              <div className="signin-brand">
                <img src="/icon-192.png" alt="" width={72} height={72} />
                <h1>Save n Make</h1>
              </div>
              <p className="muted">
                Everything you saved and meant to cook, in one place — with a hand through the actual cooking.
              </p>
            </div>
            {!signingOut && <SignIn onSignedIn={refreshUser} />}
          </>
        )}

        {user && screen.at === 'tabs' && (
          <>
            {/* A cook left running is the most important thing on the screen. */}
            {!!running.length && tab !== 'save' && (
              <div className="outcome partial" style={{ marginBottom: 20 }}>
                <h3><Icon name="flame" size={18} /> Still cooking</h3>
                {running.map((s) => (
                  <div className="filter-row" key={s.id}>
                    <b>{s.snapshot.title}</b>
                    <span className="muted small">step {s.currentStep + 1} of {s.snapshot.steps.length}</span>
                    {!!s.timers.filter((t) => t.status !== 'stopped').length && (
                      <span className="chip"><Icon name="timer" size={13} /> {s.timers.filter((t) => t.status !== 'stopped').length} running</span>
                    )}
                    <button type="button" className="primary" style={{ width: 'auto', marginLeft: 'auto' }} onClick={() => cook(s.recipeId, s.id)}>
                      Pick it back up
                    </button>
                  </div>
                ))}
              </div>
            )}

            {tab === 'discover' && summary && (
              <KitchenHero summary={summary} buddy={buddy} recipes={recipes} onBuddy={setBuddy} />
            )}
            {tab === 'discover' && (
              <Waiting recipes={recipes} onChanged={reload} onOpen={(r) => openRecipe(r.id)} />
            )}
            {tab === 'discover' && (
              <Discover categories={categories} libraryVersion={recipes} onOpen={(r) => openRecipe(r.id)} onCook={(r) => cook(r.id)} />
            )}
            {tab === 'library' && (
              <LibraryTable
                recipes={recipes} categories={categories}
                onChanged={reload} onOpen={(r) => openRecipe(r.id)} onCook={(r) => cook(r.id)}
              />
            )}
            {tab === 'save' && <Import onImported={reload} onOpenRecipe={openRecipe} onCook={(id) => cook(id)} />}
            {tab === 'kitchen' && (
              <Kitchen
                recipes={recipes} categories={categories}
                onChanged={reload} onOpen={(r) => openRecipe(r.id)} onCook={(r) => cook(r.id)}
                canTriage={user.capabilities.includes('admin.access')}
                summary={summary} buddy={buddy} onBuddy={setBuddy}
              />
            )}
            {tab === 'account' && <Account user={user} />}
            {tab === 'admin' && <Admin />}
          </>
        )}

        {user && screen.at === 'recipe' && (
          <RecipeEditor
            recipeId={screen.id}
            onBack={backToTabs}
            onCook={(id) => cook(id)}
            onChanged={reload}
          />
        )}

        {user && screen.at === 'cook' && (
          <Cook
            recipeId={screen.recipeId}
            sessionId={screen.sessionId}
            onDone={backToTabs}
            onOpenRecipe={openRecipe}
          />
        )}
      </main>

      {/* Always reachable. The moment something is annoying is the moment it
          gets described accurately. */}
      {user && (
        <FeedbackButton
          screen={screenName}
          recipeId={screen.at === 'recipe' ? screen.id : screen.at === 'cook' ? screen.recipeId : null}
        />
      )}

      {moments[0] && (
        <MomentModal moment={moments[0]} buddy={buddy} onClose={dismissMoment} />
      )}

      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}
      <InstallPrompt />

      <footer className="muted small">
        {/*
          * Real links, not router state. These three are static pages served
          * outside the SPA — the app has no URL router, so anything handled in
          * React would show a signed-out reviewer the sign-in screen instead.
          * Both stores check that a human can actually reach them.
          */}
        <nav className="legal">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/support">Support</a>
        </nav>
        <BuildInfo health={health} />
        {' · '}
        <button type="button" onClick={refreshApp}>{t('update.refresh')}</button>
      </footer>
    </ErrorBoundary>
  )
}
